import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'koji_cookie_consent';

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

export function CookieConsent() {
  const [show, setShow] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) setShow(true);
  }, []);

  const saveConsent = (prefs: Omit<ConsentPreferences, 'timestamp'>) => {
    const consent: ConsentPreferences = {
      ...prefs,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setShow(false);
    if (prefs.analytics && typeof window !== 'undefined') {
      // Dispatch custom event for analytics initialization
      window.dispatchEvent(new CustomEvent('koji-analytics-consent', { detail: true }));
    }
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const acceptNecessary = () => saveConsent({ necessary: true, analytics: false, marketing: false });

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] bg-[#0a0a0a] border-t border-[#1a1a1a] p-4 md:p-6">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex-1">
          <p className="text-sm text-[#94a3b8]">
            We use cookies to enhance your experience, analyze site traffic, and assist in our marketing efforts.{" "}
            <Link to="/privacy" className="text-[#e5ac41] hover:underline">Learn more</Link>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPreferences(true)}
            className="text-sm text-[#94a3b8] hover:text-[#f1f5f9] px-4 py-2 rounded-lg border border-[#1a1a1a] hover:border-[#64748b] transition-all"
          >
            Manage Preferences
          </button>
          <button
            onClick={acceptNecessary}
            className="text-sm text-[#94a3b8] px-4 py-2 rounded-lg border border-[#1a1a1a] hover:bg-[#111111] transition-all"
          >
            Reject All
          </button>
          <button
            onClick={acceptAll}
            className="text-sm font-semibold text-[#0a0a0a] px-5 py-2 rounded-lg bg-[#e5ac41] hover:bg-[#d39737] transition-all"
          >
            Accept All
          </button>
        </div>
      </div>

      {showPreferences && (
        <ConsentPreferencesModal
          onSave={(prefs) => saveConsent(prefs)}
          onClose={() => setShowPreferences(false)}
        />
      )}
    </div>
  );
}

function ConsentPreferencesModal({
  onSave,
  onClose,
}: {
  onSave: (prefs: Omit<ConsentPreferences, 'timestamp'>) => void;
  onClose: () => void;
}) {
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  return (
    <div className="fixed inset-0 z-[101] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#111111] border border-[#1a1a1a] rounded-2xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-[#f1f5f9] mb-4">Cookie Preferences</h3>
        <div className="space-y-4 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-[#f1f5f9]">Necessary</p>
              <p className="text-xs text-[#64748b]">Required for the site to function. Cannot be disabled.</p>
            </div>
            <div className="w-10 h-6 rounded-full bg-[#e5ac41] flex items-center justify-center">
              <div className="w-4 h-4 rounded-full bg-white" />
            </div>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-[#f1f5f9]">Analytics</p>
              <p className="text-xs text-[#64748b]">Helps us understand how visitors interact with our website.</p>
            </div>
            <button
              onClick={() => setAnalytics(!analytics)}
              className={`w-10 h-6 rounded-full transition-colors ${analytics ? 'bg-[#e5ac41]' : 'bg-[#1a1a1a]'}`}
            >
              <div className={`w-4 h-4 rounded-full bg-white transition-transform ${analytics ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-[#f1f5f9]">Marketing</p>
              <p className="text-xs text-[#64748b]">Used to deliver personalized advertisements and measure their effectiveness.</p>
            </div>
            <button
              onClick={() => setMarketing(!marketing)}
              className={`w-10 h-6 rounded-full transition-colors ${marketing ? 'bg-[#e5ac41]' : 'bg-[#1a1a1a]'}`}
            >
              <div className={`w-4 h-4 rounded-full bg-white transition-transform ${marketing ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="text-sm text-[#94a3b8] px-4 py-2 rounded-lg border border-[#1a1a1a] hover:bg-[#111111] transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ necessary: true, analytics, marketing })}
            className="text-sm font-semibold text-[#0a0a0a] px-5 py-2 rounded-lg bg-[#e5ac41] hover:bg-[#d39737] transition-all"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}
